import { Capacitor, registerPlugin } from '@capacitor/core';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../auth/firebase';
import { requestNativeVoipPermissions } from './voipPermissions';

const FUNCTIONS_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net';

interface SyncReachabilityPlugin {
  start(opts: { accessToken: string; identity: string }): Promise<void>;
  stop(): Promise<void>;
  registerNativeAuth(opts: { customToken: string }): Promise<void>;
}

// Registered at module scope (matches the working InlineCamera pattern) — a
// registerPlugin() call made lazily inside an async function does not reliably
// bridge to the native plugin registered in MainActivity.onCreate().
const SyncReachability: SyncReachabilityPlugin =
  (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })?.Capacitor?.Plugins?.['SyncReachability'] as SyncReachabilityPlugin
  ?? registerPlugin<SyncReachabilityPlugin>('SyncReachability');

function waitForCurrentUser(): Promise<User | null> {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise(resolve => {
    const timer = setTimeout(() => { unsub(); resolve(null); }, 5000);
    const unsub = onAuthStateChanged(auth, user => {
      clearTimeout(timer);
      unsub();
      resolve(user);
    });
  });
}

// Collapses concurrent/rapid-fire callers (e.g. React effects re-running during
// auth hydration) into one in-flight registration — without this, each caller
// independently created its own Sync client for the same identity, and the
// resulting parallel connect/disconnect races appear to be exactly what
// suppresses Twilio's reachability webhook (confirmed via device logs showing
// simultaneous "connecting Sync client" calls and a document-creation 409
// conflict between them).
let inFlight: Promise<void> | null = null;
/**
 * Timestamp of the last completed registration. Two independent triggers —
 * the visibilitychange handler (foreground resume ≥2min) and the login
 * useEffect (fires on any new authState object reference, not just a real
 * login/logout, since it's keyed on isLoggedIn+user.id which can get a fresh
 * object on unrelated re-renders) — were confirmed firing back-to-back
 * (~2.4s apart in Cloud Functions logs) for the same identity. The inFlight
 * guard above only catches truly overlapping calls; it does nothing once the
 * first call has already resolved. Each redundant call mints a brand new
 * Twilio access token and re-runs CapacitorTwilioVoice.login() (which has no
 * reachability-aware short-circuit, unlike SyncReachabilityManager.start()'s
 * already-Connected check on the native side) — re-registering the Voice SDK
 * repeatedly for no reason. This cooldown skips a redundant re-registration
 * for the same identity when the last one succeeded recently.
 */
let lastRegisteredAt = 0;
let lastRegisteredIdentity: string | null = null;
const REGISTRATION_COOLDOWN_MS = 60_000;

/**
 * Registers the device with Twilio Voice SDK for incoming VoIP calls.
 * Uses @capgo/capacitor-incoming-call-kit for the native incoming call UI on both
 * Android (ConnectionService + full-screen intent) and iOS (CallKit).
 */
export function registerTwilioVoip(): Promise<void> {
  if (inFlight) {
    console.log('[TwilioVoIP] registerTwilioVoip() already in flight — reusing');
    return inFlight;
  }
  inFlight = doRegisterTwilioVoip().finally(() => { inFlight = null; });
  return inFlight;
}

async function doRegisterTwilioVoip(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const platform = Capacitor.getPlatform();
  console.log('[TwilioVoIP] registerTwilioVoip() starting on', platform);

  try {
    // prompt: true — must request RECORD_AUDIO here, at registration time,
    // not reactively when a call rings. IncomingCallActivity.onAccept() also
    // requests it if somehow still missing, but that path blocks invite.accept()
    // behind the system permission dialog — long enough on a first-ever accept
    // that Twilio's server-side invite timeout fires and the caller hears a
    // busy/no-answer signal before the user even finishes tapping "Allow".
    await requestNativeVoipPermissions({ prompt: true });

    const user = await waitForCurrentUser();
    const idToken = await user?.getIdToken();
    if (!idToken) {
      console.warn('[TwilioVoIP] No authenticated Firebase user — skipping registration');
      return;
    }

    const resp = await fetch(`${FUNCTIONS_URL}/getTwilioVoiceToken?platform=${platform}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!resp.ok) {
      console.warn('[TwilioVoIP] Token fetch failed:', resp.status);
      return;
    }
    const { token, identity } = await resp.json();

    const sinceLast = Date.now() - lastRegisteredAt;
    if (identity === lastRegisteredIdentity && sinceLast < REGISTRATION_COOLDOWN_MS) {
      console.log(`[TwilioVoIP] Skipping redundant re-registration for ${identity} (last one ${Math.round(sinceLast / 1000)}s ago)`);
      return;
    }
    console.log('[TwilioVoIP] Registering as identity:', identity);

    const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');

    await CapacitorTwilioVoice.login({ accessToken: token });
    console.log('[TwilioVoIP] Registered for incoming calls');
    lastRegisteredAt = Date.now();
    lastRegisteredIdentity = identity;

    // Keep a Sync client connected under the same identity so Twilio's Sync
    // reachability webhook can report this device online/offline in near
    // real time — lets the IVR skip a dead VoIP ring and go straight to
    // PSTN fallback instead of discovering unreachability mid-call.
    if (platform === 'android') {
      try {
        await SyncReachability.start({ accessToken: token, identity });
        console.log('[TwilioVoIP] Sync reachability client started');
      } catch (syncErr) {
        console.warn('[TwilioVoIP] Sync reachability start failed:', syncErr);
      }

      // Give native code (KeepAliveService) its own durable, auto-refreshing
      // Firebase session so it can keep renewing the Sync token and the
      // reachability connection even with the app fully closed for days —
      // mirrors the persistence the JS Firebase Auth session already has,
      // just on the native side where the WebView isn't running to help.
      try {
        const mintResp = await fetch(`${FUNCTIONS_URL}/mintNativeAuthToken`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (mintResp.ok) {
          const { customToken } = await mintResp.json();
          await SyncReachability.registerNativeAuth({ customToken });
          console.log('[TwilioVoIP] Native auth session registered');
        } else {
          console.warn('[TwilioVoIP] mintNativeAuthToken failed:', mintResp.status);
        }
      } catch (nativeAuthErr) {
        console.warn('[TwilioVoIP] Native auth registration failed:', nativeAuthErr);
      }
    }

  } catch (err) {
    console.error('[TwilioVoIP] Registration failed:', err);
  }
}

